/**
 * API Client
 *
 * Typed fetch wrapper for all backend calls.
 * Handles auth redirects and error parsing.
 */

import type { ApiResponse } from '@shared/types.js';
import { isSignInPath } from '@shared/return-path.js';
import { returnPathFor } from './return-path.js';

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const isFormData = options.body instanceof FormData;
  const headers: Record<string, string> = isFormData
    ? {} // Let browser set Content-Type with boundary
    : { 'Content-Type': 'application/json' };

  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...headers,
      ...(options.headers as Record<string, string>),
    },
  });

  // Auth redirect
  if (res.status === 401) {
    // If not on login page, redirect
    if (!isSignInPath(window.location.pathname)) {
      // Keep the page, so signing in comes back to it (LH-1508).
      const back = returnPathFor(window.location.pathname, window.location.search, window.location.hash);
      window.location.href = back === '/' ? '/login' : `/login?redirect=${encodeURIComponent(back)}`;
    }
    throw new ApiError('Authentication required', 401);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
    throw new ApiError(body.error || res.statusText, res.status, body);
  }

  return res.json() as Promise<T>;
}

export const api = {
  get<T>(url: string): Promise<ApiResponse<T>> {
    return request<ApiResponse<T>>(url);
  },

  post<T>(url: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<ApiResponse<T>>(url, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  put<T>(url: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<ApiResponse<T>>(url, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  delete<T>(url: string): Promise<ApiResponse<T>> {
    return request<ApiResponse<T>>(url, { method: 'DELETE' });
  },

  postForm<T>(url: string, formData: FormData): Promise<ApiResponse<T>> {
    // Don't set Content-Type — browser sets it with boundary for multipart
    return request<ApiResponse<T>>(url, {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },
};

export { ApiError };
